import { ContentfulLogo } from '@stadionhq-v3/contentful-ui';
import {
  AroundTheGroundsClub,
  AroundTheGroundsCompetition,
  AroundTheGroundsLiveScoreRow,
} from '@stadionhq-v3/football-ui';
import {
  MATCH_STATUS_AROUND_THE_GROUNDS,
  MatchStatusAroundTheGroundsType,
} from '@stadionhq-v3/core';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { Asset } from '@graphql/__generated__/graphql';
import { assetTransform } from '@utils/transforms/components/atoms/asset.transform';
import { AblyMutator } from '@stadionhq-v3/ably';
import dayjs from '@lib/dayjs';
import { Locale } from '@core/constants/locales.constants';

export type AroundTheGroundsAdditionalTranslations = {
  title: string;
  buttonText: string;
};

/**
 * Match states the live-scores feed emits for each fixture (one per row).
 * This is the wire contract for
 * `AroundTheGroundsLiveScoreRowResponseApi.matchState`.
 */
export const LIVE_SCORE_MATCH_STATUS = {
  CANCELLED: 'Cancelled',
  POSTPONED: 'Postponed',
  SUSPENDED: 'Suspended',
  PRE_MATCH: 'PreMatch',
  FIRST_HALF: 'FirstHalf',
  HALF_TIME: 'HalfTime',
  SECOND_HALF: 'SecondHalf',
  EXTRA_TIME_FIRST_HALF: 'ExtraTimeFirstHalf',
  EXTRA_TIME_HALF_TIME: 'ExtraTimeHalfTime',
  EXTRA_TIME_SECOND_HALF: 'ExtraTimeSecondHalf',
  END_OF_90_MINS: 'EndOf90Mins',
  END_OF_EXTRA_TIME: 'EndOfExtraTime',
  PENALTY_SHOOT_OUT: 'PenaltyShootOut',
  FULL_TIME: 'FullTime',
  UNKNOWN: 'Unknown',
} as const;

export type LiveScoreMatchStatus =
  (typeof LIVE_SCORE_MATCH_STATUS)[keyof typeof LIVE_SCORE_MATCH_STATUS];

/**
 * The shared `AroundTheGroundsInlineGrouped` component understands only its own
 * 7-value enum (`MATCH_STATUS_AROUND_THE_GROUNDS`), which drives both its
 * grouping (Pre Match / Live / Post Match via `MATCH_STATUS_AROUND_THE_GROUNDS_MAP`)
 * and its per-row label (only `FULL_TIME` → "FT", `HALF_TIME` → "HT" and
 * `POSTPONED` → "PP" get a label; every other state shows the kickoff time).
 *
 * Because that component can't be changed from here, we collapse the richer
 * live-scores states onto it:
 *   - all in-play phases (incl. extra time, end-of-period, penalties) → `LIVE`
 *   - both half-time variants → `HALF_TIME` ("HT")
 *   - `FullTime` → `FULL_TIME` ("FT")
 *   - `PreMatch` / `Unknown` → `PRE_MATCH` (kickoff time)
 *   - `Cancelled` / `Suspended` have no dedicated label, so they reuse the
 *     `POSTPONED` treatment ("PP", Pre Match group) rather than a misleading
 *     kickoff time. (The shared component has no Cancelled/Suspended label.)
 */
const LIVE_SCORE_STATUS_TO_AROUND_THE_GROUNDS: Record<
  LiveScoreMatchStatus,
  MatchStatusAroundTheGroundsType
> = {
  [LIVE_SCORE_MATCH_STATUS.PRE_MATCH]:
    MATCH_STATUS_AROUND_THE_GROUNDS.PRE_MATCH,
  [LIVE_SCORE_MATCH_STATUS.UNKNOWN]: MATCH_STATUS_AROUND_THE_GROUNDS.PRE_MATCH,
  [LIVE_SCORE_MATCH_STATUS.CANCELLED]:
    MATCH_STATUS_AROUND_THE_GROUNDS.POSTPONED,
  [LIVE_SCORE_MATCH_STATUS.POSTPONED]:
    MATCH_STATUS_AROUND_THE_GROUNDS.POSTPONED,
  [LIVE_SCORE_MATCH_STATUS.SUSPENDED]:
    MATCH_STATUS_AROUND_THE_GROUNDS.POSTPONED,
  [LIVE_SCORE_MATCH_STATUS.FIRST_HALF]: MATCH_STATUS_AROUND_THE_GROUNDS.LIVE,
  [LIVE_SCORE_MATCH_STATUS.SECOND_HALF]: MATCH_STATUS_AROUND_THE_GROUNDS.LIVE,
  [LIVE_SCORE_MATCH_STATUS.EXTRA_TIME_FIRST_HALF]:
    MATCH_STATUS_AROUND_THE_GROUNDS.LIVE,
  [LIVE_SCORE_MATCH_STATUS.EXTRA_TIME_SECOND_HALF]:
    MATCH_STATUS_AROUND_THE_GROUNDS.LIVE,
  [LIVE_SCORE_MATCH_STATUS.END_OF_90_MINS]:
    MATCH_STATUS_AROUND_THE_GROUNDS.LIVE,
  [LIVE_SCORE_MATCH_STATUS.END_OF_EXTRA_TIME]:
    MATCH_STATUS_AROUND_THE_GROUNDS.LIVE,
  [LIVE_SCORE_MATCH_STATUS.PENALTY_SHOOT_OUT]:
    MATCH_STATUS_AROUND_THE_GROUNDS.LIVE,
  [LIVE_SCORE_MATCH_STATUS.HALF_TIME]:
    MATCH_STATUS_AROUND_THE_GROUNDS.HALF_TIME,
  [LIVE_SCORE_MATCH_STATUS.EXTRA_TIME_HALF_TIME]:
    MATCH_STATUS_AROUND_THE_GROUNDS.HALF_TIME,
  [LIVE_SCORE_MATCH_STATUS.FULL_TIME]:
    MATCH_STATUS_AROUND_THE_GROUNDS.FULL_TIME,
};

/**
 * Maps a live-scores feed match state to the value the shared Around the Grounds
 * component expects. Falls back to `PRE_MATCH` for any unrecognised value.
 */
export const mapLiveScoreStatusToAroundTheGrounds = (
  status: LiveScoreMatchStatus
): MatchStatusAroundTheGroundsType =>
  LIVE_SCORE_STATUS_TO_AROUND_THE_GROUNDS[status] ??
  MATCH_STATUS_AROUND_THE_GROUNDS.PRE_MATCH;

export interface AroundTheGroundsClubResponseApi {
  id: string;
  name: string;
  shortName: string;
  shortCode: string | null;
  crestImageUrl: string | null;
  crestInverseImageUrl: string | null;
}

export type AroundTheGroundsFeedApiResponse = {
  clientTeamDataProviderId: string;
  rows: AroundTheGroundsLiveScoreRowResponseApi[];
  competition: AroundTheGroundsCompetitionResponseApi;
  snapshotDate: Date;
};

export type AroundTheGroundsCompetitionResponseApi = {
  id?: string;
  name?: string | null;
  logoImageUrl?: string;
  logoInverseImageUrl?: string;
};

export interface AroundTheGroundsLiveScoreRowResponseApi {
  fixtureId: string;
  homeClub: AroundTheGroundsClubResponseApi;
  awayClub: AroundTheGroundsClubResponseApi;
  homeScore: number | null;
  awayScore: number | null;
  matchState: LiveScoreMatchStatus;
  kickoff: string;
  homeAggScore: number | null;
  awayAggScore: number | null;
  awayPenaltyScore: number | null;
  homePenaltyScore: number | null;
}

/** A fixture row as held in component state (plain data, no React nodes). */
export interface AroundTheGroundsStoredRow {
  fixtureId: string;
  homeClub: AroundTheGroundsClubResponseApi;
  awayClub: AroundTheGroundsClubResponseApi;
  homeScore: number | null;
  awayScore: number | null;
  // Collapsed onto the shared component's vocabulary (drives grouping + label).
  matchState: MatchStatusAroundTheGroundsType;
  kickoff: string;
  homeAggScore: number | null;
  awayAggScore: number | null;
  awayPenaltyScore: number | null;
  homePenaltyScore: number | null;
}

/**
 * Component state for the Around the Grounds feed.
 *
 * Rows are keyed by `fixtureId` (not an array) so that single-fixture Ably
 * updates merge cleanly: the Ably store deep-merges every `setAblyData` payload,
 * so a message carrying `{ rowsById: { [id]: row } }` only touches that one
 * fixture's key — concurrent updates for different fixtures can't clobber each
 * other, and no message needs to carry the whole table. Rows are stored as plain
 * data (no React nodes), so the deep-merge never has to merge elements; the
 * `teamLogo`/competition view shapes are built at render time. The render layer
 * converts this back to the `rows` array the component expects.
 */
export type AroundTheGroundsLiveProps = {
  rowsById: Record<string, AroundTheGroundsStoredRow>;
  competition?: AroundTheGroundsCompetitionResponseApi;
};

/**
 * Two payload shapes flow through the mutator:
 *  - the REST snapshot used to seed initial state (all rows), and
 *  - a single live-score row pushed over Ably (message name = fixtureId).
 */
export type AroundTheGroundsAblyResponse =
  | AroundTheGroundsFeedApiResponse
  | AroundTheGroundsLiveScoreRowResponseApi;

const isFeedResponse = (
  response: AroundTheGroundsAblyResponse
): response is AroundTheGroundsFeedApiResponse => 'rows' in response;

/**
 * Transforms a single live-score row (from the snapshot or an Ably push) into
 * the plain row held in state: match state collapsed onto the shared component's
 * vocabulary and the kickoff normalised to a UTC ISO string (with a `Z`
 * designator). The football-ui component formats kickoff with
 * `dayjs(kickoff).format('HH:mm')`, which renders in the user's local timezone —
 * but only when the value is unambiguously UTC. Without this, naive API strings
 * are parsed as local time and shown as UTC. Idempotent for `Z`/offset inputs.
 */
export const transformLiveScoreRow = (
  rowItem: AroundTheGroundsLiveScoreRowResponseApi,
  locale: Locale
): AroundTheGroundsStoredRow => ({
  ...rowItem,
  matchState: mapLiveScoreStatusToAroundTheGrounds(rowItem.matchState),
  kickoff: rowItem.kickoff
    ? dayjs(locale).utc(rowItem.kickoff).toISOString()
    : rowItem.kickoff,
});

export const aroundTheGroundsAblyMutator =
  (
    locale: Locale
  ): AblyMutator<AroundTheGroundsLiveProps, AroundTheGroundsAblyResponse> =>
  ({ state, response }): AroundTheGroundsLiveProps => {
    // Single fixture pushed over Ably: update it in place, keyed by fixtureId,
    // and return the full set. We can't emit only the updated key and rely on the
    // store merge — the initial REST snapshot lives in the Ably fetch slice while
    // live messages write the (separate) live slice, which the store reads in
    // preference once it exists. So the live slice must carry the whole table;
    // otherwise the first update would shadow the snapshot and collapse the list.
    if (!isFeedResponse(response)) {
      const currentRows = state?.rowsById;

      // Ignore updates for fixtures not already in the table (seeded by the
      // snapshot). Returning the current state leaves the live slice untouched —
      // and when there is no state yet, returns undefined so nothing is written
      // and the snapshot isn't shadowed by an empty slice.
      if (!currentRows || !(response.fixtureId in currentRows)) {
        return state;
      }

      return {
        rowsById: {
          ...currentRows,
          [response.fixtureId]: transformLiveScoreRow(response, locale),
        },
        competition: state.competition,
      };
    }

    // REST snapshot: seed/replace the full set of rows, keyed by fixtureId.
    const { rows, competition } = response;

    return {
      rowsById: (rows ?? []).reduce<Record<string, AroundTheGroundsStoredRow>>(
        (acc, rowItem) => {
          acc[rowItem.fixtureId] = transformLiveScoreRow(rowItem, locale);
          return acc;
        },
        {}
      ),
      competition,
    };
  };

const buildClubLogo = (
  club: AroundTheGroundsClubResponseApi,
  fallbackClubCrest?: CleanContentful<Asset>
): AroundTheGroundsClub['teamLogo'] =>
  club.crestImageUrl ? (
    <ContentfulLogo
      width={16}
      height={16}
      url={club.crestImageUrl}
      alt={club.name || ''}
    />
  ) : fallbackClubCrest ? (
    <ContentfulLogo
      {...assetTransform(fallbackClubCrest)}
      width={16}
      height={16}
    />
  ) : undefined;

const toComponentClub = (
  club: AroundTheGroundsClubResponseApi,
  fallbackClubCrest?: CleanContentful<Asset>
): AroundTheGroundsClub => {
  // Prefer the short name, falling back to the full name when it's missing or
  // empty. The live feed can send an empty `shortName` (e.g. national teams),
  // which would otherwise leave the row's name blank.
  const displayName = club.shortName || club.name;

  return {
    id: club.id,
    name: displayName,
    shortName: displayName,
    shortCode: club.shortCode,
    teamLogo: buildClubLogo(club, fallbackClubCrest),
  };
};

/**
 * Builds the `rows` array (with team-crest nodes) the shared component renders
 * from the keyed-by-fixture state. Called at render time, not in the mutator, so
 * React nodes never enter the merge-able store.
 */
export const toAroundTheGroundsRows = (
  rowsById: Record<string, AroundTheGroundsStoredRow> | undefined,
  fallbackClubCrest?: CleanContentful<Asset>
): AroundTheGroundsLiveScoreRow[] =>
  Object.values(rowsById ?? {}).map((row) => ({
    ...row,
    homeClub: toComponentClub(row.homeClub, fallbackClubCrest),
    awayClub: toComponentClub(row.awayClub, fallbackClubCrest),
  }));

/** Builds the component `competition` prop (logo intentionally omitted). */
export const toAroundTheGroundsCompetition = (
  competition: AroundTheGroundsCompetitionResponseApi | undefined
): AroundTheGroundsCompetition => ({
  id: competition?.id,
  name: competition?.name,
  competitionLogo: undefined,
});
